import { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import FileUpload from './components/FileUpload';
import ConfigPanel from './components/ConfigPanel';
import PageGrid from './components/PageGrid';
import PreviewModal from './components/PreviewModal';
import EditPanel from './components/EditPanel';
import logo from "./assets/Logo.png";


pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export default function CelcomPipelineMerger() {
  const mnoOptions=['Safaricom','Airtel','Telkom','Finserve'];
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pages, setPages] = useState([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedMnos, setSelectedMnos] = useState([]);
  const baseUrl = import.meta.env.VITE_BACKEND_URL || '';
  // Parameter states to map matching template file keys
  const [senderId, setSenderId] = useState('');
  const [clientName, setClientName] = useState('');
  const [gridMessage, setGridMessage] = useState(null);

  // Reader Lightbox States
  const [activePreviewPage, setActivePreviewPage] = useState(null);

  const handleSenderIdKeyUp=(e)=>{
   ;
    if(e.target.value.length<=11){
      e.target.style.borderColor = 'green';
    }else {
      e.target.style.borderColor = 'red';
      e.target.style.borderWidth='2px';
    }
  };


  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedMnos([...selectedMnos, value]);
    } else {
      setSelectedMnos(selectedMnos.filter((item) => item !== value));
    }
         console.log(selectedMnos);

  };
  
  const handleGetStamp = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/stamp`);
      if (!response.ok) throw new Error('Failed to fetch stamp');
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || 'stamp.png';
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error downloading stamp: ' + error.message);
    }
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    const files = Array.from(input.files);
    const pdfFiles = files.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      input.value = '';
      return;
    }
    setIsProcessing(true);
    setPages([]);
    setGridMessage(null);

    setUploadedFiles(prev => {
      const existingNames = prev.map(f => f.name);
      const uniqueNewFiles = pdfFiles.filter(f => !existingNames.includes(f.name));
      return [...prev, ...uniqueNewFiles];
    });

    try {
      for (const file of pdfFiles) {
        await new Promise((resolve) => {
          const fileReader = new FileReader();
          fileReader.onload = async (event) => {
            const typedArray = new Uint8Array(event.target.result);
            const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
            const newPages = [];

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              
              // Capture original PDF page dimensions at scale=1 for coordinate mapping
              const origViewport = page.getViewport({ scale: 1 });

              // Low scale for workspace dashboard grid
              const viewport = page.getViewport({ scale: 0.35 });
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              await page.render({ canvasContext: context, viewport }).promise;
              const lowResSrc = canvas.toDataURL();

              // High scale for the full-screen reader preview modal
              const readerViewport = page.getViewport({ scale: 2 });
              canvas.height = readerViewport.height;
              canvas.width = readerViewport.width;
              await page.render({ canvasContext: context, viewport: readerViewport }).promise;
              const highResSrc = canvas.toDataURL();

              newPages.push({
                id: `p-${file.name}-${i}-${Math.random()}`,
                originFile: file.name,
                pageNumber: i,
                src: lowResSrc,
                fullResSrc: highResSrc, // Added for reading mode
                origWidth: origViewport.width,
                origHeight: origViewport.height,
              });
            }
            setPages(prev => [...prev, ...newPages]);
            resolve();
          };
          fileReader.readAsArrayBuffer(file);
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
      setActivePreviewPage(null);
      input.value = '';
    }
  };

  const handleDragStart = (idx) => setDraggedItemIndex(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (draggedItemIndex === null || draggedItemIndex === idx) return;
    const currentList = [...pages];
    const [targetItem] = currentList.splice(draggedItemIndex, 1);
    currentList.splice(idx, 0, targetItem);
    setPages(currentList);
    setDraggedItemIndex(null);
  };

  const compileAndDownload = async () => {
    if (pages.length === 0) return;
    if (!senderId.trim()) {
      alert("Please specify an Alpha Numeric Sender ID parameter before combining.");
      return;
    }
    if (selectedMnos.length === 0) {
      alert("Please select at least one mobile network operator.");
      return;
    }
    if (selectedMnos.includes('Telkom') && senderId.includes(' ')) {
      alert("Sender ID cannot contain spaces when Telkom is selected.");
      return;
    }
    setIsProcessing(true);

    try {
      const formData = new FormData();

      uploadedFiles.forEach(file => {
        formData.append('files', file);
      });

      const orderBlueprint = pages.map(({ originFile, pageNumber }) => ({ originFile, pageNumber }));
      formData.append('orderBlueprint', JSON.stringify(orderBlueprint));
      formData.append('senderId', senderId);
      formData.append('clientName', clientName);
      formData.append('selectedMnos', JSON.stringify(selectedMnos));

      const response = await fetch(`${baseUrl}/api/v2/merge-pdf`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Stitching task error returned from server.');

      const fileBlob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const downloadFilename = filenameMatch?.[1] || `${senderId}.${fileBlob.type === 'application/zip' ? 'zip' : 'pdf'}`;
      const clientDownloadUrl = URL.createObjectURL(fileBlob);
      const downloadLinkAnchor = document.createElement('a');
      downloadLinkAnchor.href = clientDownloadUrl;
      downloadLinkAnchor.download = downloadFilename;
      document.body.appendChild(downloadLinkAnchor);
      downloadLinkAnchor.click();
      downloadLinkAnchor.remove();
      URL.revokeObjectURL(clientDownloadUrl);
      
      setPages([]);
      setGridMessage(`${senderId} uploaded`);

    } catch (error) {
      alert('Error rendering production processing pipeline: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container-fluid shadow-sm-1 px-4 bg-light" style={{ maxWidth: '1540px' }}>
      <nav className="navbar navbar-expand navbar-dark bg-primary rounded-3 shadow-sm p-2 mb-4 mt-2">
        <div className="container-fluid px-0">
          <a className="navbar-brand fw-bold mb-0" href="#">
            <img src={logo} alt="logo" className="ms-2 rounded-2" width="100" height="40" />
            <i> DPT</i>
          </a>
        </div>
      </nav>

      <div className="card shadow border-0 p-4">
        <div className="row g-4">
          <div className="col-12 col-xl-8 border-end">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-3">
              <div className="d-grid d-sm-block">
                <FileUpload onFileChange={handleFileChange} isProcessing={isProcessing} baseUrl={baseUrl} />
                <button type="button" className={' btn btn-success mx-2 mb-2'} onClick={() => setIsEditPanelOpen(true)}>
              <i className="bi bi-pencil-square me-2"></i>Edit PDF
            </button>
              </div>
            </div>

            <PageGrid 
              pages={pages}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              setActivePreviewPage={setActivePreviewPage}
              draggedItemIndex={draggedItemIndex}
              message={gridMessage}
              baseUrl={baseUrl}
            />
          </div>

          <div className="col-12 col-xl-4 d-flex flex-column justify-content-between" style={{ minHeight: '350px' }}>
            <ConfigPanel 
              senderId={senderId}
              setSenderId={setSenderId}
              clientName={clientName}
              setClientName={setClientName}
              mnoOptions={mnoOptions}
              handleSenderIdKeyUp={handleSenderIdKeyUp}
              handleCheckboxChange={handleCheckboxChange}
              selectedMnos={selectedMnos}
              baseUrl={baseUrl}
            />

            <div className="mt-auto">
              {isProcessing && (
                <div className="text-center my-3 text-primary fw-medium small">
                  <div className="spinner-border spinner-border-sm me-2 text-primary" role="status"></div>
                  Processing and generating configuration layers...
                </div>
              )}

              <div className="d-grid gap-2">
                <button 
                  className="btn btn-info py-2 fw-bold shadow-sm" 
                  onClick={handleGetStamp}
                >
                  <i className="bi bi-image me-2"></i>Get Stamp
                </button>
                <button 
                  className="btn btn-success py-2 fw-bold shadow-sm" 
                  onClick={compileAndDownload} 
                  disabled={pages.length === 0 || isProcessing}
                >
                  <i className="bi bi-cloud-arrow-down-fill me-2"></i>Compile & Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditPanelOpen && (
        <div 
          className="modal fade show d-block" 
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '96vw' }}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-dark text-white py-2">
                <h6 className="modal-title fw-bold m-0">
                  <i className="bi bi-pencil-square me-2 text-info"></i>PDF Editor
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setIsEditPanelOpen(false)}
                />
              </div>
              <div className="modal-body bg-secondary p-3" style={{ minHeight: '82vh' }}>
                <EditPanel pages={pages} uploadedFiles={uploadedFiles} isProcessing={isProcessing} baseUrl={baseUrl} onEditSuccess={(fileName) => {
                  setPages([]);
                  setGridMessage(`${fileName} edited`);
                  setIsEditPanelOpen(false);
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <PreviewModal activePreviewPage={activePreviewPage} onClose={() => setActivePreviewPage(null)} baseUrl={baseUrl} />
    </div>
  );
}
